// /src/pages/Team.js
import { useEffect, useState } from "react";
import { UserPlus, Mail, Phone } from "lucide-react";

export default function Team() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("sales_rep");
  const [inviting, setInviting] = useState(false);
  const [message, setMessage] = useState({ text: "", ok: true });

  useEffect(() => {
    const token = localStorage.getItem("token");

    fetch("/api/me", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res.json())
      .then(data => { if (!data.error) setCurrentUser(data); })
      .catch(() => {});

    fetch("/api/users", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res.json())
      .then(data => {
        setMembers(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const isAdmin = currentUser?.role === "admin";

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail) return;
    setInviting(true);
    setMessage({ text: "", ok: true });

    try {
      const res = await fetch("/api/inviteuser", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ email: inviteEmail, role: inviteRole }),
      });
      const data = await res.json();

      if (!res.ok || data.error) {
        const detail = data.detail || data.resend_response || data.note || "";
        setMessage({ text: `${data.error || "Failed to send invite."}${detail ? ` — ${detail}` : ""}`, ok: false });
      } else if (data.warning) {
        setMessage({ text: `⚠️ ${data.warning} — Link: ${data.inviteLink}`, ok: true });
      } else {
        setMessage({ text: `Invite sent to ${inviteEmail}`, ok: true });
        setInviteEmail("");
      }
    } catch {
      setMessage({ text: "Network error. Please try again.", ok: false });
    }

    setInviting(false);
  };

  const roleBadge = (role) => {
    const r = role ?? "sales_rep";
    return <span className={`badge badge-${r}`}>{r.replace("_", " ")}</span>;
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Team</div>
          <div className="page-subtitle">Manage sales reps, managers, and admins.</div>
        </div>
      </div>

      {isAdmin && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-header"><span className="card-title">Invite Team Member</span></div>
          <div className="card-body">
            <form onSubmit={handleInvite} style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
              <div className="form-group" style={{ flex: 1, minWidth: 200, marginBottom: 0 }}>
                <label className="form-label">Email</label>
                <input
                  className="form-input"
                  type="email"
                  placeholder="colleague@company.com"
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                  required
                />
              </div>
              <div className="form-group" style={{ width: 160, marginBottom: 0 }}>
                <label className="form-label">Role</label>
                <select className="form-select" value={inviteRole} onChange={e => setInviteRole(e.target.value)}>
                  <option value="sales_rep">Sales Rep</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <button className="btn btn-primary" type="submit" disabled={inviting}>
                <UserPlus size={14} /> {inviting ? "Sending..." : "Send Invite"}
              </button>
            </form>
            {message.text && (
              <p style={{ marginTop: 10, fontSize: 13, color: message.ok ? "#16a34a" : "#ef4444" }}>
                {message.text}
              </p>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <p style={{ color: "#64748b" }}>Loading team...</p>
      ) : (
        <div className="team-grid">
          {members.map(m => (
            <div key={m.id} className="team-card">
              <div className="team-avatar">{(m.full_name?.[0] ?? m.email?.[0] ?? "?").toUpperCase()}</div>
              <div className="team-name">{m.full_name ?? "—"}</div>
              <div className="team-email">{m.email}</div>
              {roleBadge(m.role)}
              {m.status === "invited" && (
                <span style={{ fontSize: 11, color: "#f59e0b", background: "#fef3c7", padding: "2px 8px", borderRadius: 20, fontWeight: 500 }}>
                  Invite pending
                </span>
              )}
              <div className="team-actions">
                {m.email && (
                  <a href={`mailto:${m.email}`} className="btn btn-secondary btn-sm btn-icon" title="Email">
                    <Mail size={13} />
                  </a>
                )}
                {m.phone && (
                  <a href={`tel:${m.phone}`} className="btn btn-secondary btn-sm btn-icon" title="Call">
                    <Phone size={13} />
                  </a>
                )}
              </div>
              {m.id === currentUser?.id && (
                <span style={{ fontSize: 11, color: "#94a3b8" }}>You</span>
              )}
            </div>
          ))}
          {members.length === 0 && (
            <p style={{ color: "#64748b", fontSize: 13 }}>No team members yet.</p>
          )}
        </div>
      )}
    </div>
  );
}